<template>
  <GoogleMap
    :api-key="googleMapKey"
    style="width: 100%; height: 500px"
    :center="mapCenterPosition"
    :zoom="15"
    :fullscreen-control="!isDraggableMarker"
    :street-view-control="!isDraggableMarker"
    :map-type-control="!isDraggableMarker"
  >
    <ClientOnly>
      <CustomMarker
        v-if="!isDraggableMarker"
        :options="{
          position: mapCenterPositionModel,
          anchorPoint: 'BOTTOM_CENTER',
        }"
      >
        <div style="text-align: center" :style="markerColorFilter">
          <img src="/cdn/content/map-marker-alt.svg" width="50" height="50" alt="" style="margin-top: 8px" />
        </div>
      </CustomMarker>
    </ClientOnly>
    <Marker
      v-if="isDraggableMarker"
      :options="{
        position: mapCenterPositionModel,
        anchorPoint: 'BOTTOM_CENTER',
        draggable: true,
      }"
      @dragend="onMarkerDragEnd"
    />
  </GoogleMap>
</template>

<script lang="ts">
import { GoogleMap, Marker, CustomMarker } from 'vue3-google-map';
import type { PropType } from 'vue';
import { convertToFilter } from '~/units/core/providers/colors.provider';
import { debounce } from '~/units/core/providers/debounce.provider';
import { SubComponentMixin } from '~/mixins/subComponent.mixin';
import type { GoogleMapLocation } from '~/units/addresses/types';

export default defineNuxtComponent({
  name: 'NeuronGoogleMap',
  components: { GoogleMap, Marker, CustomMarker },

  props: {
    mapCenterPosition: {
      required: true,
      type: Object as PropType<GoogleMapLocation>,
    },

    markerColor: {
      required: true,
    },

    isDraggableMarker: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  mixins: [SubComponentMixin],

  data() {
    return {
      handler: null,
    };
  },

  computed: {
    mapCenterPositionModel: {
      get() {
        return this.mapCenterPosition;
      },
      set(value: GoogleMapLocation) {
        this.$emit('update:mapCenterPosition', value);
      },
    },

    googleMapKey() {
      const nuxtApp = useNuxtApp();
      return nuxtApp.$config.public.GOOGLE_MAPS_KEY;
    },

    markerColorFilter() {
      return {
        '--primary-color-filter': `${convertToFilter(this.markerColor)}`,
      };
    },
  },

  mounted() {
    this.createHandler();
  },

  methods: {
    createHandler() {
      if (!this.handler) {
        this.handler = debounce(this.onPositionChanged, 500);
      }
    },

    onPositionChanged() {
      if (this.mapCenterPosition) {
        this.$emit('onPositionChanged');
      }
    },

    onMarkerDragEnd(e: any) {
      const newPosition = { ...e.latLng };

      this.mapCenterPositionModel = {
        lat: newPosition.lat(),
        lng: newPosition.lng(),
      };

      this.handler();
    },
  },
});
</script>

<style scoped>
:deep([src='/cdn/content/map-marker-alt.svg']) {
  filter: var(--primary-color-filter);
}
</style>
